import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

interface Message {
  id: number;
  sender_name: string;
  subject: string | null;
  body: string;
  created_at: string;
  is_read: boolean;
  property_id: number | null;
  property_title: string | null;
}

interface MessagesResponse {
  data: Message[];
}

interface Agent {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface AgentsResponse {
  data: Agent[];
  meta?: { total: number };
}

interface PropertyOption {
  id: number;
  title: string;
}

interface PropertiesResponse {
  data: PropertyOption[];
}

type TabType = 'inquiry' | 'internal';

export default function MessagesPage(): React.ReactElement {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<TabType>('inquiry');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [newMessage, setNewMessage] = useState({
    recipient_id: '',
    subject: '',
    property_id: '',
    body: '',
  });
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast((s) => ({ ...s, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // Fetch messages
  const { data: messagesData, isLoading } = useQuery({
    queryKey: ['messages', activeTab],
    queryFn: () => api.get<MessagesResponse>(`/v1/messages?type=${activeTab}`),
  });

  const messages = messagesData?.data ?? [];

  // Fetch team members for internal messaging
  const { data: agentsData } = useQuery({
    queryKey: ['company-team'],
    queryFn: () => api.get<AgentsResponse>('/v1/companies/me/team'),
    enabled: activeTab === 'internal',
  });

  const agents = agentsData?.data ?? [];

  // Fetch properties for message form
  const { data: propertiesData } = useQuery({
    queryKey: ['properties-list'],
    queryFn: () => api.get<PropertiesResponse>('/v1/properties'),
    enabled: showNewMessage,
  });

  const properties = propertiesData?.data ?? [];

  // Mark as read
  const markReadMutation = useMutation({
    mutationFn: (messageId: number) => api.patch(`/v1/messages/${messageId}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  // Send internal message
  const sendMutation = useMutation({
    mutationFn: (data: { recipient_user_id: number; subject?: string; property_id?: number; body: string }) =>
      api.post('/v1/messages/internal', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', 'internal'] });
      setShowNewMessage(false);
      setNewMessage({ recipient_id: '', subject: '', property_id: '', body: '' });
      setToast({ show: true, message: t('messages.sent'), type: 'success' });
    },
    onError: () => {
      setToast({ show: true, message: t('messages.sendError'), type: 'error' });
    },
  });

  const handleExpand = (msg: Message): void => {
    if (expandedId === msg.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(msg.id);
    if (!msg.is_read) {
      markReadMutation.mutate(msg.id);
    }
  };

  const handleSend = (): void => {
    if (!newMessage.recipient_id || !newMessage.body.trim()) return;
    sendMutation.mutate({
      recipient_user_id: Number(newMessage.recipient_id),
      subject: newMessage.subject || undefined,
      property_id: newMessage.property_id ? Number(newMessage.property_id) : undefined,
      body: newMessage.body.trim(),
    });
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">{t('nav.messages')}</h1>
        {activeTab === 'internal' && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowNewMessage(true)}
          >
            + {t('messages.new')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div role="tablist" className="tabs tabs-bordered mb-6">
        <button
          role="tab"
          className={`tab ${activeTab === 'inquiry' ? 'tab-active' : ''}`}
          onClick={() => { setActiveTab('inquiry'); setExpandedId(null); setShowNewMessage(false); }}
        >
          {t('messages.inquiries')}
        </button>
        <button
          role="tab"
          className={`tab ${activeTab === 'internal' ? 'tab-active' : ''}`}
          onClick={() => { setActiveTab('internal'); setExpandedId(null); setShowNewMessage(false); }}
        >
          {t('messages.internal')}
        </button>
      </div>

      {/* New message form */}
      {showNewMessage && activeTab === 'internal' && (
        <div className="card bg-base-100 shadow-md mb-6">
          <div className="card-body">
            <h3 className="card-title text-base mb-2">
              {t('messages.compose')}
            </h3>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('messages.recipient')} *</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={newMessage.recipient_id}
                onChange={(e) => setNewMessage((m) => ({ ...m, recipient_id: e.target.value }))}
              >
                <option value="" disabled>
                  {t('messages.selectRecipient')}
                </option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.first_name} {agent.last_name} ({agent.role === 'agent' ? 'Agente' : agent.role === 'company_admin' ? 'Administrador' : agent.role})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('messages.subject')}</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder={t('messages.subjectPlaceholder')}
                value={newMessage.subject}
                onChange={(e) => setNewMessage((m) => ({ ...m, subject: e.target.value }))}
              />
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('messages.property')}</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={newMessage.property_id}
                onChange={(e) => setNewMessage((m) => ({ ...m, property_id: e.target.value }))}
              >
                <option value="">
                  {t('messages.noProperty')}
                </option>
                {properties.map((prop) => (
                  <option key={prop.id} value={prop.id}>
                    {prop.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('messages.body')} *</span>
              </label>
              <textarea
                className="textarea textarea-bordered w-full"
                rows={4}
                placeholder={t('messages.bodyPlaceholder')}
                value={newMessage.body}
                onChange={(e) => setNewMessage((m) => ({ ...m, body: e.target.value }))}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowNewMessage(false)}
              >
                {t('buttons.cancel')}
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSend}
                disabled={!newMessage.recipient_id || !newMessage.body.trim() || sendMutation.isPending}
              >
                {sendMutation.isPending ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  t('messages.send')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages list */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skeleton h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-lg text-base-content/60">
            {t('messages.empty')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`card bg-base-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${
                !msg.is_read ? 'border-l-4 border-l-primary' : ''
              }`}
              onClick={() => handleExpand(msg)}
            >
              <div className="card-body p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-semibold text-sm ${!msg.is_read ? 'text-primary' : ''}`}>
                        {msg.sender_name}
                      </span>
                      {!msg.is_read && (
                        <span className="badge badge-primary badge-xs">
                          {t('messages.unread')}
                        </span>
                      )}
                      {msg.property_title && (
                        <span className="badge badge-outline badge-xs truncate max-w-[150px]">
                          {msg.property_title}
                        </span>
                      )}
                    </div>
                    {msg.subject && (
                      <p className="text-sm font-medium mb-1">{msg.subject}</p>
                    )}
                    {expandedId !== msg.id ? (
                      <p className="text-sm text-base-content/60 truncate">{msg.body}</p>
                    ) : (
                      <p className="text-sm text-base-content/80 whitespace-pre-line mt-2">{msg.body}</p>
                    )}
                  </div>
                  <span className="text-xs text-base-content/50 whitespace-nowrap">
                    {formatDate(msg.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${toast.type === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{toast.message}</span>
            <button
              className="btn btn-ghost btn-xs"
              onClick={() => setToast((s) => ({ ...s, show: false }))}
            >
              &#10005;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
